import type { RepairBlockedReason } from '@realplc/types';
import type { Message } from '../types';
import { deliveryValidationLabel, type DeliveryLabelLanguage } from './deliveryValidationLabel';

/**
 * `skipped` means there was nothing to do. `needs_review` means there is
 * something to do that a machine is not allowed to do — a distinction the
 * engineer has to be able to see, because the outstanding errors are real.
 */
export type RepairDisplayStatus =
  | 'idle'
  | 'running'
  | 'completed'
  | 'completed_with_warnings'
  | 'failed'
  | 'needs_review'
  | 'skipped';

export interface RepairBlockedContext {
  reason: RepairBlockedReason;
  /** Phrases in the request that triggered the block. Empty when unknown. */
  signals: string[];
}

type RepairMessage = Pick<Message, 'isStreaming' | 'progressState' | 'repairAppendix' | 'pipeline' | 'parsed' | 'stageBuffers'>;

function hasText(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function normalizeStatus(value: unknown): string {
  return typeof value === 'string' ? value.trim().toLowerCase() : '';
}

/**
 * Every `repairAppendix.status` value that settles the outcome on its own,
 * mapped to the `RepairDisplayStatus` it resolves to. The one list that says
 * which repair statuses are final — `getRepairDisplayStatus`'s early checks
 * and `isRepairAppendixAuthoritative` both read it, so the two cannot drift
 * apart the way they did before this refactor (TA10b review #4).
 *
 * Three keys collapse to `'skipped'`: `skipped`, `disabled` and
 * `not_applicable` have always meant the same thing to the reader — there
 * was nothing for automatic repair to do — even though the runtime has used
 * different words for it.
 */
const TERMINAL_REPAIR_APPENDIX_STATUS_RESULT: ReadonlyMap<string, RepairDisplayStatus> = new Map([
  ['completed', 'completed'],
  ['completed_with_warnings', 'completed_with_warnings'],
  ['skipped', 'skipped'],
  ['disabled', 'skipped'],
  ['not_applicable', 'skipped'],
  ['needs_review', 'needs_review'],
  ['failed', 'failed'],
]);

/** Whether a normalized `repairAppendix.status` value is one of the terminal ones above. */
export function isTerminalRepairAppendixStatus(status: string): boolean {
  return TERMINAL_REPAIR_APPENDIX_STATUS_RESULT.has(status);
}

export function hasFinalRepairValidation(message: RepairMessage): boolean {
  return Boolean(message.repairAppendix?.finalValidation);
}

export function getEffectiveValidationReport(message: RepairMessage): any {
  // Final post-repair validation is the latest validation truth and must win
  // over the initial validation report. Otherwise the timeline/workspace can
  // keep showing “validation failed” after the repaired validation has passed.
  return message.repairAppendix?.finalValidation
    || message.pipeline?.validationReport
    || message.parsed?.verification
    || null;
}

/**
 * Why a repair was blocked, when it was.
 *
 * Read from the fields the runtime sets rather than from its prose. Messages
 * persisted before `blockedReason` existed still render the card — they just
 * cannot name the phrase that triggered it.
 */
export function getRepairBlockedContext(message: RepairMessage): RepairBlockedContext | undefined {
  const appendix = message.repairAppendix;
  if (normalizeStatus(appendix?.status) !== 'needs_review') return undefined;
  return {
    reason: appendix?.blockedReason || 'stage_policy',
    signals: appendix?.blockedSignals?.filter(hasText) || [],
  };
}

export function hasRepairAppendixContent(message: RepairMessage): boolean {
  const appendix = message.repairAppendix;
  return Boolean(
    appendix
      && (
        normalizeStatus(appendix.status) !== '' && normalizeStatus(appendix.status) !== 'not_started'
          || hasText(appendix.summary)
          || hasText(appendix.finalCode)
          || Boolean(appendix.finalValidation)
          || Boolean(appendix.attempts?.length)
          || Boolean(appendix.timings)
      ),
  );
}

/**
 * Whether the repair loop ended with a program that passed — the fact a
 * finished preflight row consults before colouring its own failure red.
 */
export function isRepairOutcomeSuccess(message: RepairMessage): boolean {
  const status = getRepairDisplayStatus(message);
  return status === 'completed' || status === 'completed_with_warnings';
}

/**
 * Whether the repair loop is over, ahead of the fields that are supposed to
 * say so.
 *
 * 2026-09-28 production, two turns: the worker calls
 * `publishStageDone('repair')` the moment the loop stops, but its last
 * progress frame is still `phase: 'repairing'` and `repairAppendix.status`
 * stays `'repairing'` until the whole turn ends — ten to twelve minutes
 * later, through the final validation and the delivery notes. The stage
 * buffer's own `done` flag is the one fact that is actually true the moment
 * the loop stops; `progressState.stage === 'final_checks'` is the runtime's
 * next-stage frame (TA10a) and says the same thing a step earlier.
 */
export function isRepairStageDone(message: RepairMessage): boolean {
  return message.stageBuffers?.repair?.done === true
    || normalizeStatus(message.progressState?.stage) === 'final_checks';
}

/**
 * Whether `repairAppendix` itself has reached a terminal verdict — its own
 * `status`, or a `finalValidation` — as opposed to `getRepairDisplayStatus`
 * resolving to a terminal-looking value only because the stage buffer's own
 * status decided ahead of it (the `repairStageDone` branch below).
 *
 * A caller that echoes a status-derived sentence as though the appendix had
 * confirmed it — the timeline's own summary, a step's conclusion — needs to
 * tell the two apart: a sentence conjured from the buffer alone can say more
 * than is actually known yet, or can crowd out something more specific (or a
 * deliberate silence) the appendix's own fields already carry.
 */
export function isRepairAppendixAuthoritative(message: RepairMessage): boolean {
  return isTerminalRepairAppendixStatus(normalizeStatus(message.repairAppendix?.status))
    || Boolean(message.repairAppendix?.finalValidation);
}

export function getRepairDisplayStatus(message: RepairMessage): RepairDisplayStatus {
  const appendix = message.repairAppendix;
  const status = normalizeStatus(appendix?.status);
  const phase = normalizeStatus(message.progressState?.phase);
  const stage = normalizeStatus(message.progressState?.stage);
  const finalValidation = appendix?.finalValidation as any;
  const finalValidationExists = Boolean(finalValidation);
  const finalValidationPassed = finalValidationExists && finalValidation.valid !== false;
  const hasFinalCode = hasText(appendix?.finalCode);
  const hasAttempts = Boolean(appendix?.attempts?.length);
  const repairStageDone = isRepairStageDone(message);
  const terminalResult = TERMINAL_REPAIR_APPENDIX_STATUS_RESULT.get(status);

  // Every terminal status except `needs_review` and `failed` settles the
  // outcome outright, in the exact word `TERMINAL_REPAIR_APPENDIX_STATUS_RESULT`
  // gives it. Those two are excluded here because each has its own ordering
  // requirement against the final-validation check just below — see the
  // comments at their own checks.
  if (terminalResult && terminalResult !== 'needs_review' && terminalResult !== 'failed') return terminalResult;
  // Ahead of the final-validation check below: a blocked repair always carries
  // the failing validation that provoked it, and letting that decide would
  // report the outcome as a plain failure with the reason stripped off.
  if (terminalResult === 'needs_review') return 'needs_review';

  // Some backend paths mark progress as failed/repair after the final validation
  // object has already been attached. In that case the final validation is newer
  // than the initial validation/progress label and should drive visible state.
  if (finalValidationExists) {
    if (finalValidationPassed) {
      return status === 'failed' ? 'completed_with_warnings' : 'completed';
    }
    return 'failed';
  }

  if (terminalResult === 'failed') return 'failed';

  const terminalPhase = phase === 'completed' || phase === 'failed' || phase === 'cancelled' || phase === 'canceled';
  // Gated by `repairStageDone`: once the stage buffer (or the runtime's own
  // next-stage frame) says the loop is over, `status`/`phase`/`stage` lagging
  // behind must not read as "running" — see `isRepairStageDone`.
  const repairStageActive = !repairStageDone
    && (status === 'repairing' || phase === 'repairing' || stage === 'repair');
  if (repairStageActive && !terminalPhase) return 'running';

  if (hasFinalCode) return hasAttempts ? 'completed_with_warnings' : 'completed';

  // Review fix (2026-09-29 production): an in-loop appendix carries no
  // `finalCode`, so once the repair stage is done but repairAppendix.status
  // has not caught up yet (TA10b), the `hasAttempts` guess below cannot tell
  // "no round was applied because none succeeded" from "no round was applied
  // because none was needed" — it called both `completed_with_warnings`,
  // claiming success before the real outcome was known. The stage buffer's
  // own status is the worker's actual verdict on the loop it just closed, so
  // once the stage is done it outranks the guess.
  //
  // Not scoped to `message.isStreaming`: every terminal `repairAppendix.status`
  // already returns above this point, so by construction this only runs when
  // the appendix is stuck non-terminal — live or not. `hydrateSessionMessages`
  // never sets `isStreaming` on a reloaded row, so a reload or a second device
  // watching the same turn during its final-checks window needs this exactly
  // as much as the live tab does.
  //
  // I-2 (review of 8305b621): a 'failed' buffer word is a failure regardless
  // of the turn's own phase, so it always wins here. A 'completed' buffer word
  // is different — the turn's own phase reaching 'failed' is a later,
  // whole-turn signal (a stream stall, a crash after the repair loop otherwise
  // finished) than the buffer's word, so it overrides 'completed' into
  // 'failed' rather than letting it stand. This can no longer be left to the
  // `hasAttempts` guess further down: with no round ever recorded
  // (`attempts: []`), `hasAttempts` is false, so a phase-failed turn with a
  // 'completed' buffer word fell through everything below to 'idle' — losing
  // the failure — and a 'failed' buffer word never even got consulted, for
  // exactly the same reason.
  if (repairStageDone) {
    const bufferStatus = normalizeStatus(message.stageBuffers?.repair?.status);
    if (bufferStatus === 'failed') return 'failed';
    if (bufferStatus === 'completed') return phase === 'failed' ? 'failed' : 'completed_with_warnings';
  }

  if (hasAttempts && terminalPhase) return 'failed';
  if (hasAttempts) return (message.isStreaming && !repairStageDone) ? 'running' : 'completed_with_warnings';

  return 'idle';
}

export function isRepairDisplayRunning(message: RepairMessage): boolean {
  return getRepairDisplayStatus(message) === 'running';
}

export function getRepairTitleKey(status: RepairDisplayStatus): string {
  switch (status) {
    case 'completed':
      return 'Automatic Repair Completed';
    case 'completed_with_warnings':
      return 'Automatic Repair Completed With Warnings';
    case 'failed':
      return 'Automatic Repair Needs Review';
    case 'needs_review':
      return 'Automatic Repair Blocked By Policy';
    case 'skipped':
      return 'Automatic Repair Skipped';
    case 'running':
      return 'Automatic Repair In Progress';
    default:
      return 'Repair';
  }
}

export function getRepairTimelineSummary(status: RepairDisplayStatus, review?: unknown, language?: DeliveryLabelLanguage): string | undefined {
  switch (status) {
    case 'completed':
      return deliveryValidationLabel('修复后校验通过', review, language);
    case 'completed_with_warnings':
      return '修复完成，需复核遗留告警';
    case 'failed':
      return '修复未通过，需人工复核';
    case 'needs_review':
      return '按安全策略停用自动修复，需人工复核';
    case 'skipped':
      return '自动修复已跳过';
    case 'running':
      return '自动修复执行中';
    default:
      return undefined;
  }
}
